import React from 'react';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import { CircleDisplay } from 'src/modules/shared/components/atoms/circle-display';
import { SiWhatsapp } from 'react-icons/si';
import Tag from 'src/modules/shared/components/atoms/tag';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import { PiCalendarLight, PiTrashLight } from 'react-icons/pi';
import { IntegrationConnectionStatus } from 'src/api/generated';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface DisconnectedIntegrationCardProps {
  integrationProviderId: string;
  displayName?: string;
  connectionStatus: IntegrationConnectionStatus;
  statusUpdatedAt?: string;
  statusReason?: string;
  onboardingWarning?: string;
  removalBlocked?: boolean;
  removing?: boolean;
  onRemove: () => void;
  /** Permission-aware recovery or assisted-offboarding actions. */
  actions?: React.ReactNode;
}

export const DisconnectedIntegrationCard: React.FC<
  DisconnectedIntegrationCardProps
> = ({
  integrationProviderId,
  displayName,
  connectionStatus,
  statusUpdatedAt,
  statusReason,
  onboardingWarning,
  removalBlocked = false,
  removing = false,
  onRemove,
  actions,
}) => {
  const { t } = useI18n();
  const statusTag =
    connectionStatus === IntegrationConnectionStatus.INACTIVE
      ? {
          content: t('settings.integrations.whatsapp.accounts.inactive'),
          variant: 'warning' as const,
        }
      : {
          content: t('settings.integrations.status.error'),
          variant: 'danger' as const,
        };

  return (
    <Card shadow="md" variant="primary" appearance="outline">
      <CardHeader>
        <div className="flex items-center gap-3">
          <CircleDisplay
            icon={<SiWhatsapp />}
            variant="whatsapp-subtle"
            size="md"
          />

          <div className="flex-1 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Text size="h6" weight="medium">
                {displayName ??
                  t('settings.integrations.whatsapp.accounts.defaultName')}
              </Text>
              <Tag
                content={statusTag.content}
                variant={statusTag.variant}
                appearance="soft"
                size="xs"
              />
            </div>

            <Text size="small" color="secondary" weight="regular">
              {t('settings.integrations.whatsapp.accounts.identifier', {
                id: integrationProviderId,
              })}
            </Text>

            {statusUpdatedAt && (
              <div className="flex items-center gap-2 text-secondary-600">
                <PiCalendarLight className="h-4 w-4" />
                <Text size="small" color="secondary" weight="regular">
                  {t('settings.integrations.whatsapp.accounts.updatedAt', {
                    date: formatDate(statusUpdatedAt),
                  })}
                </Text>
              </div>
            )}
          </div>

          {!removalBlocked ? (
            <Button
              variant="danger"
              size="sm"
              disabled={removing}
              onClick={onRemove}
            >
              <div className="flex items-center gap-2">
                <PiTrashLight className="h-4 w-4" />
                {t('shared.actions.delete')}
              </div>
            </Button>
          ) : null}
        </div>
      </CardHeader>

      {statusReason || onboardingWarning || actions ? (
        <CardBody>
          <div className="space-y-3">
            {onboardingWarning ? (
              <Alert
                role="alert"
                size="sm"
                variant="warning"
                message={onboardingWarning}
              />
            ) : null}
            {statusReason ? (
              <Text size="small" color="secondary" weight="regular">
                {t('settings.integrations.whatsapp.accounts.reason', {
                  reason: statusReason,
                })}
              </Text>
            ) : null}
            {actions}
          </div>
        </CardBody>
      ) : null}
    </Card>
  );
};
